import { useLayoutEffect, useState, type RefObject } from "react";

/**
 * How many of `itemCount` optional trailing items currently fit inside
 * `containerRef` without it overflowing (`scrollWidth > clientWidth`) — the
 * rest should be hidden and offered from a fallback "..." menu instead
 * (TopBar.tsx's project actions: Rename/Save as/Export/Delete, previously
 * always tucked in that menu even when the bar had plenty of empty room).
 *
 * Two effects, deliberately asymmetric:
 * - One runs after EVERY render and only ever SHRINKS `visibleCount` (by
 *   one, if still overflowing) — safe from infinite loops since it can only
 *   move in one direction, bounded by `itemCount`.
 * - The other, on mount/resize/`itemCount` change only, optimistically
 *   resets `visibleCount` back up to `itemCount` — a fresh attempt at
 *   showing everything, which the shrink effect then walks back down if it
 *   doesn't actually fit.
 *
 * Growth was originally attempted inside the same per-render effect as the
 * shrink (try +1 whenever there was slack) — that oscillated forever
 * between "fits" and "doesn't" once a render caused by the shrink itself
 * looked like fresh slack again, and tripped React's "Maximum update depth
 * exceeded" guard. Restricting growth to real external triggers (not every
 * render) is what actually breaks that cycle.
 *
 * Bumping a discarded `useState` counter (`setResizeTick` below — its own
 * getter is never even named, only the setter is used) is the other real
 * trap here: resizing to a narrower width while `visibleCount` already
 * equals `itemCount` (nothing has shrunk yet) makes the reset's
 * `setVisibleCount(itemCount)` a same-value no-op — React bails out and
 * skips the render entirely, so the shrink effect never gets a chance to
 * re-measure the now-smaller `clientWidth` at all. Bumping a counter that's
 * guaranteed to always change forces that extra render (and so the fresh
 * measurement) regardless of whether `visibleCount`'s own value happens to
 * move.
 */
/**
 * Whether `el`'s content no longer fits — the +1 absorbs subpixel rounding
 * noise between `scrollWidth`/`clientWidth` so a pixel-perfect fit doesn't
 * register as overflow (a real trap: without it, some zoom levels/DPI
 * ratios would shrink one item further than actually necessary, forever).
 */
export function isOverflowing(el: { scrollWidth: number; clientWidth: number }): boolean {
  return el.scrollWidth > el.clientWidth + 1;
}

export function useToolbarOverflow(
  containerRef: RefObject<HTMLElement | null>,
  itemCount: number
): number {
  const [visibleCount, setVisibleCount] = useState(itemCount);
  const [, setResizeTick] = useState(0);

  // Deliberately no dependency array — needs to re-measure after EVERY
  // render (a resize is only one of several reasons the bar's content width
  // can change; a renamed project or a badge appearing/disappearing is
  // just as real a trigger, and none of those go through the other effect
  // below). Monotonic-shrink-only keeps this safe despite that — see the
  // doc comment above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (isOverflowing(el) && visibleCount > 0) setVisibleCount(visibleCount - 1);
  });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setVisibleCount(itemCount);
    const observer = new ResizeObserver(() => {
      setResizeTick((tick) => tick + 1);
      setVisibleCount(itemCount);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef, itemCount]);

  return Math.min(visibleCount, itemCount);
}
